import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { AppShell } from "../components/AppShell";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useMyDashboard } from "../lib/api";
import { ROLE_LABEL_KEYS } from "../lib/auth";
import { localeFor } from "../lib/format";
import { queueHref } from "../lib/o2c";
import { entityLabel, modulesFor } from "../lib/nav";
import EmployeeSelfService from "./EmployeeSelfService";

/** Where a work block and its rows open (the block keys come from onesphere.api.my.dashboard).
 *
 * `record` is the OneSphere screen that shows that one record. A block without one opens its list instead: the rows
 * used to open the generated `/erp/<DocType>/<name>` screen, and those screens were removed from the application on
 * 2026-09-28 — nothing in here links into ERPNext. */
const BLOCK_TO: Record<string, { list: string; record?: (name: string) => string }> = {
  actions: { list: "/executive" },
  exceptions: { list: "/executive" },
  // the held queue, and one held order opened in it (OS-0672) — it used to land on the order form without the order
  held_orders: { list: queueHref("held"), record: (n) => queueHref("held", n) },
  statements_to_approve: { list: "/orders?tab=incentives", record: (n) => `/orders/statement/${encodeURIComponent(n)}` },
  statements_to_pay: { list: "/orders?tab=incentives", record: (n) => `/orders/statement/${encodeURIComponent(n)}` },
  overdue_invoices: { list: "/orders?tab=collections" },
  onboarding_in_qualification: { list: "/procure?tab=suppliers", record: (n) => `/procure/onboarding/${encodeURIComponent(n)}` },
  requests_open: { list: "/procure?tab=requests", record: (n) => `/procure/request/${encodeURIComponent(n)}` },
  awards_to_approve: { list: "/projects/sourcing", record: (n) => `/projects/sourcing/${encodeURIComponent(n)}` },
  leave_to_approve: { list: "/people/lifecycle/approvals" },
  my_quotations: { list: "/orders?tab=crm&view=quotations" },
};

/** My dashboard (requirements 9.1, 9.2): who I am, what waits for me (permission-aware counts by role and scope), the modules
 * my role opens. The first page after sign-in for people who work in one line rather than across the group. */
export default function MyDashboard() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const { identity } = useAuth();
  const [tab, setTab] = useState<"work" | "self">("work");
  const q = useMyDashboard();
  const data = q.data;
  const roles = identity?.roles ?? [];
  const modules = modulesFor(roles).filter((m) => m.key !== "my");
  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "");
  const scopeValues = Object.entries(data?.scope ?? {});
  const total = (data?.blocks ?? []).reduce((n, b) => n + b.count, 0);
  return (
    <AppShell subtitle={t("nav.my.title")}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{t("my.title")}</h1>
          <p className="text-sm text-brand-grey">{t("my.subtitle")}</p>
        </div>
        <div className="text-right text-xs text-brand-grey" data-my-identity>
          <div className="text-sm font-semibold text-brand-charcoal">{data?.full_name ?? identity?.name}</div>
          <div>{roles.length > 0 ? roles.map((r) => t(ROLE_LABEL_KEYS[r] ?? r)).join(" · ") : t("my.noRoles")}</div>
          <div className="text-brand-primary">{t("my.scope")}: {scopeValues.length > 0 ? scopeValues.map(([k, v]) => `${k}: ${v.join(", ")}`).join(" · ") : t("my.wholeGroup")}</div>
        </div>
      </div>
      <div className="mt-4 flex gap-1 border-b border-brand-grey-light" role="tablist" aria-label="my-sections">
        {(["work", "self"] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-grey hover:text-brand-charcoal"}`}>
            {k === "work" ? t("my.work") : t("my.selfService")}
          </button>
        ))}
      </div>
      {tab === "self" && <div className="mt-6"><EmployeeSelfService /></div>}
      {tab === "work" && <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2" data-my-work>
          <div className="flex items-baseline justify-between">
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("my.work")}</h2>
            {data && <span className="text-xs text-brand-grey">{total} {t("my.items")}</span>}
          </div>
          {q.isError && <div className="mt-3"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>}
          {q.isLoading && <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>}
          {data && data.blocks.length === 0 && <Card className="mt-3 px-4 py-6 text-sm text-brand-grey">{t("my.nothing")}</Card>}
          {data && data.blocks.length > 0 && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {data.blocks.map((b) => {
                const to = BLOCK_TO[b.key];
                return (
                  <Card key={b.key} data-my-block={b.key} className={`px-4 py-3 ${b.count === 0 ? "opacity-70" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-brand-charcoal">{t(`my.blocks.${b.key}`)}</div>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.count > 0 ? "bg-brand-primary text-white" : "bg-brand-grey-light text-brand-grey"}`}>{b.count}</span>
                    </div>
                    {b.items.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs">
                        {b.items.map((it) => (
                          <li key={it.name} className="flex items-baseline justify-between gap-2">
                            <Link to={to ? (to.record ? to.record(it.name) : to.list) : "#"} className="min-w-0 truncate text-brand-charcoal hover:text-brand-primary hover:underline" title={it.title ?? it.name}>
                              {it.title ?? it.name}{it.subtitle && <span className="text-brand-grey"> · {it.subtitle}</span>}
                            </Link>
                            {it.date && <span className="shrink-0 text-brand-grey">{fmtDate(it.date)}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {to && <Link to={to.list} className="mt-2 inline-block text-xs font-semibold text-brand-primary hover:underline">{t("my.open")} ›</Link>}
                  </Card>
                );
              })}
            </div>
          )}
        </section>
        <section data-my-modules>
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("my.modules")}</h2>
          <div className="mt-3 space-y-2">
            {modules.map((m) => (
              <Card key={m.key} className="px-4 py-3">
                <Link to={m.to} className="text-sm font-semibold text-brand-charcoal hover:text-brand-primary hover:underline">{t(m.label)}</Link>
                {m.entities.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-brand-grey">
                    {m.entities.slice(0, 6).map((e) => <Link key={e.key} to={e.to} className="hover:text-brand-primary hover:underline">{entityLabel(e, lng, t)}</Link>)}
                    {m.entities.length > 6 && <Link to={m.to} className="text-brand-primary">+{m.entities.length - 6}</Link>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      </div>}
    </AppShell>
  );
}
